import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Building2, Loader2, Mail, Plus, X, ArrowLeft, ExternalLink, Save, FileText } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import billingService, { describeTerm, xeroInvoiceUrl } from '../../services/billing';
import subscriptionService from '../../services/subscription';
import type { SubscriptionPlan } from '../../types/subscription';
import type { BillingOrgDetail, BillingSubscription } from '../../types/billing';
import { BillingGate, ErrorBanner, Field, InvoiceStatusBadge } from '../../components/billing/billingUi';
import { errorText, inputClass, useBillingAccess } from '../../components/billing/billingHelpers';
import TrialPlanPicker from '../../components/billing/TrialPlanPicker';
import { draftsToTrials, type TrialDraft } from '../../components/billing/trialDrafts';
import TermFields from '../../components/billing/TermFields';
import { emptyTermDraft, termDraftProblem, termDraftToInput, type TermDraft } from '../../components/billing/termDrafts';
import ActivateSubscriptionModal from '../subscriptions/ActivateSubscriptionModal';
import { useOrgTimezone, formatDateInTz, formatLocalDateInTz } from '@/utils/timezone';

const money = (n: unknown) => subscriptionService.formatCurrency(Number(n) || 0);
const todayStr = () => new Date().toISOString().slice(0, 10);

/** /billing/organisations/:orgId — one customer's commercial picture. */
export default function BillingOrganisation() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const { canBill } = useBillingAccess();
  const id = Number(orgId);

  const [data, setData] = useState<BillingOrgDetail | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await billingService.getOrganisation(id);
      setData(res);
      setError(null);
    } catch (err) {
      setError(errorText(err, 'Failed to load organisation'));
    }
  }, [id]);

  useEffect(() => {
    if (canBill) {
      load();
      subscriptionService.getAvailablePlans().then(r => { if (r.success) setPlans(r.data); }).catch(() => {});
    }
  }, [canBill, load]);

  /** Run an action, then reload; errors land in the page banner. */
  const act = async (fn: () => Promise<unknown>, done?: string) => {
    setError(null);
    setNotice(null);
    try {
      await fn();
      if (done) setNotice(done);
      await load();
      return true;
    } catch (err) {
      setError(errorText(err, 'That did not work'));
      return false;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={data?.organisation.name ?? 'Organisation'}
        subtitle="Billing"
        icon={<Building2 className="h-6 w-6 text-white" />}
      />
      <BillingGate canBill={canBill}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
          <Button variant="ghost" onClick={() => navigate('/billing')}>
            <ArrowLeft className="h-4 w-4" />
            All organisations
          </Button>
          <ErrorBanner message={error} />
          {notice && <div className="bg-success-50 border border-success-200 rounded-lg p-3 text-sm text-success-800 break-words">{notice}</div>}

          {!data ? (
            !error && <Loader2 className="h-6 w-6 text-primary-600 animate-spin mx-auto my-10" />
          ) : (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ContactCard data={data} act={act} />
                <PeopleCard data={data} act={act} orgTz={orgTz} />
              </div>
              <SubscriptionsCard data={data} plans={plans} act={act} orgTz={orgTz} />
              <OffersCard data={data} plans={plans} act={act} orgTz={orgTz} />
              <HistoryCard data={data} orgTz={orgTz} />
            </>
          )}
        </div>
      </BillingGate>
    </div>
  );
}

type Act = (fn: () => Promise<unknown>, done?: string) => Promise<boolean>;

function Card({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="bg-white rounded-xl border border-neutral-200 p-5 min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h2 className="font-semibold text-neutral-900">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function ContactCard({ data, act }: { data: BillingOrgDetail; act: Act }) {
  const o = data.organisation;
  const [billingEmail, setBillingEmail] = useState(o.billing_email ?? '');
  const [phone, setPhone] = useState(o.contact_phone ?? '');
  const [abn, setAbn] = useState(o.abn ?? '');
  const dirty = billingEmail !== (o.billing_email ?? '') || phone !== (o.contact_phone ?? '') || abn !== (o.abn ?? '');
  return (
    <Card title="Billing contact">
      <div className="space-y-3">
        <Field label="Invoices go to" htmlFor="bo-billing" hint="Also updates the Xero contact">
          <input id="bo-billing" type="email" value={billingEmail} onChange={e => setBillingEmail(e.target.value)} className={inputClass} />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Phone" htmlFor="bo-phone">
            <input id="bo-phone" value={phone} onChange={e => setPhone(e.target.value)} className={inputClass} />
          </Field>
          <Field label="ABN" htmlFor="bo-abn">
            <input id="bo-abn" value={abn} onChange={e => setAbn(e.target.value)} className={inputClass} />
          </Field>
        </div>
        <p className="text-xs text-neutral-500 break-all">Contact: {o.contact_email || '—'}{o.state ? ` · ${o.state}` : ''}</p>
        {dirty && (
          <Button variant="primary" size="sm" onClick={() => act(() => billingService.updateOrganisation(o.id, {
            billing_email: billingEmail.trim() || undefined, contact_phone: phone.trim() || undefined, abn: abn.trim() || undefined,
          }), 'Billing contact saved.')}>
            <Save className="h-4 w-4" />
            Save
          </Button>
        )}
      </div>
    </Card>
  );
}

function PeopleCard({ data, act, orgTz }: { data: BillingOrgDetail; act: Act; orgTz: string }) {
  const [email, setEmail] = useState('');
  return (
    <Card title="Admins">
      {data.admins.length === 0 && data.pending_invites.length === 0 && (
        <p className="text-sm text-warning-700 mb-3">Nobody can manage this organisation yet — invite an admin.</p>
      )}
      <ul className="space-y-2 mb-4">
        {data.admins.map(a => (
          <li key={a.id} className="text-sm min-w-0">
            <span className="font-medium text-neutral-900">{a.username}</span>
            <span className="text-neutral-500 break-all"> · {a.email}</span>
          </li>
        ))}
        {data.pending_invites.map(i => (
          <li key={i.id} className="text-sm text-neutral-600 min-w-0 break-all">
            <Mail className="h-3.5 w-3.5 inline mr-1" />
            {i.invited_email} — invited, expires {formatDateInTz(i.expires_at, orgTz)}
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="Admin's email" aria-label="Admin email to invite" className={inputClass} />
        <Button variant="outline" disabled={!email.trim()} onClick={async () => {
          if (await act(() => billingService.inviteAdmin(data.organisation.id, email.trim()), `Invited ${email.trim()} as admin.`)) setEmail('');
        }}>
          Invite
        </Button>
      </div>
    </Card>
  );
}

type Panel = { subId: number; kind: 'trial' | 'suspend' | 'renewal' | 'seats' | 'cancel' } | null;

function SubscriptionsCard({ data, plans, act, orgTz }: { data: BillingOrgDetail; plans: SubscriptionPlan[]; act: Act; orgTz: string }) {
  const [panel, setPanel] = useState<Panel>(null);
  const [addingTrial, setAddingTrial] = useState(false);
  const [trialDrafts, setTrialDrafts] = useState<Record<number, TrialDraft>>({});
  const [manual, setManual] = useState(false);
  const orgId = data.organisation.id;
  const live = data.subscriptions.filter(s => s.status !== 'cancelled');
  const heldPlanIds = live.map(s => s.plan_id);

  const startTrials = async () => {
    const trials = draftsToTrials(trialDrafts);
    const ok = await act(async () => {
      for (const t of trials) await billingService.startTrial(orgId, t);
    }, `Started ${trials.length} trial${trials.length === 1 ? '' : 's'}.`);
    if (ok) { setAddingTrial(false); setTrialDrafts({}); }
  };

  return (
    <Card
      title="Subscriptions"
      action={
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setManual(true)}>
            <FileText className="h-4 w-4" />
            Manual activation
          </Button>
          <Button variant="primary" size="sm" onClick={() => setAddingTrial(v => !v)}>
            <Plus className="h-4 w-4" />
            Start a trial
          </Button>
        </div>
      }
    >
      {addingTrial && (
        <div className="mb-5 p-4 rounded-lg bg-neutral-50 border border-neutral-200 space-y-3">
          <TrialPlanPicker plans={plans} drafts={trialDrafts} onChange={setTrialDrafts} excludePlanIds={heldPlanIds} />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => { setAddingTrial(false); setTrialDrafts({}); }}>Cancel</Button>
            <Button variant="primary" size="sm" disabled={Object.keys(trialDrafts).length === 0} onClick={startTrials}>Start</Button>
          </div>
        </div>
      )}

      {data.subscriptions.length === 0 ? (
        <p className="text-sm text-neutral-500">No subscriptions yet.</p>
      ) : (
        <div className="space-y-3">
          {data.subscriptions.map(sub => (
            <SubscriptionRow key={sub.id} sub={sub} orgId={orgId} orgTz={orgTz} act={act}
              panel={panel?.subId === sub.id ? panel.kind : null}
              setPanel={kind => setPanel(kind ? { subId: sub.id, kind } : null)} />
          ))}
        </div>
      )}

      {manual && (
        <ActivateSubscriptionModal
          lockedOrg={{ id: orgId, name: data.organisation.name }}
          onClose={() => setManual(false)}
          onSuccess={() => { setManual(false); act(async () => {}, 'Subscription activated (no invoice raised).'); }}
        />
      )}
    </Card>
  );
}

function SubscriptionRow({ sub, orgId, orgTz, act, panel, setPanel }: {
  sub: BillingSubscription;
  orgId: number;
  orgTz: string;
  act: Act;
  panel: NonNullable<Panel>['kind'] | null;
  setPanel: (k: NonNullable<Panel>['kind'] | null) => void;
}) {
  const isTrial = !!sub.trial_ends_on && !sub.trial_converted_at && sub.status === 'active';
  const [date, setDate] = useState(sub.trial_ends_on ?? todayStr());
  const [reason, setReason] = useState('');
  const [cycle, setCycle] = useState<'monthly' | 'annual'>(sub.subscription_billing_cycle || 'monthly');
  const [seats, setSeats] = useState<Record<string, number>>(Object.fromEntries(sub.seats.map(s => [s.type_key, s.total_seats])));

  const monthly = Number(sub.base_price) + sub.seats.reduce((n, s) => n + Number(s.price_per_seat) * s.total_seats, 0);

  const open = (k: NonNullable<Panel>['kind']) => {
    setReason('');
    if (k === 'trial') setDate(sub.trial_ends_on ?? todayStr());
    if (k === 'renewal') setDate(sub.next_billing_date && sub.next_billing_date >= todayStr() ? sub.next_billing_date : todayStr());
    setPanel(panel === k ? null : k);
  };
  const close = () => setPanel(null);

  return (
    <div className={`rounded-lg border p-4 ${sub.status === 'cancelled' ? 'border-neutral-200 bg-neutral-50' : 'border-neutral-200'}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-medium text-neutral-900">{sub.name}</h3>
            {isTrial && <span className="text-xs bg-warning-100 text-warning-800 px-2 py-0.5 rounded-full">Trial · converts {formatLocalDateInTz(sub.trial_ends_on!, orgTz)}</span>}
            {sub.status === 'suspended' && <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded-full">Suspended</span>}
            {sub.status === 'cancelled' && <span className="text-xs bg-neutral-200 text-neutral-600 px-2 py-0.5 rounded-full">Cancelled</span>}
            {sub.status !== 'cancelled' && !sub.renewal_invoicing && <span className="text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full">Renewals not invoiced</span>}
          </div>
          <p className="text-sm text-neutral-600 mt-1">
            {sub.seats.map(s => `${s.name} ${s.assigned_seats}/${s.total_seats}`).join(' · ') || 'No seats'}
          </p>
          {sub.status !== 'cancelled' && (
            <p className="text-sm text-neutral-600">
              {money(monthly)}/month list · {sub.subscription_billing_cycle}
              {sub.renewal_invoicing && sub.next_billing_date && ` · next invoice ${formatLocalDateInTz(sub.next_billing_date, orgTz)}`}
            </p>
          )}
          {sub.suspended_reason && <p className="text-sm text-red-700 break-words">Suspended: {sub.suspended_reason}</p>}
        </div>
        {sub.status !== 'cancelled' && (
          <div className="flex flex-wrap gap-1.5">
            {isTrial && <Button variant="outline" size="sm" onClick={() => open('trial')}>Change trial end</Button>}
            {sub.status === 'active'
              ? <Button variant="outline" size="sm" onClick={() => open('suspend')}>Suspend</Button>
              : <Button variant="outline" size="sm" onClick={() => act(() => billingService.reinstate(sub.id), `${sub.name} reinstated.`)}>Reinstate</Button>}
            <Button variant="outline" size="sm" onClick={() => open('renewal')}>{sub.renewal_invoicing ? 'Renewals' : 'Turn on renewals'}</Button>
            <Button variant="outline" size="sm" onClick={() => open('seats')}>Seats</Button>
            <Button variant="ghost" size="sm" onClick={() => open('cancel')} aria-label={`Cancel ${sub.name}`}>
              <X className="h-4 w-4 text-error-600" />
            </Button>
          </div>
        )}
      </div>

      {panel && (
        <div className="mt-4 pt-4 border-t border-neutral-100 space-y-3">
          {panel === 'trial' && (
            <>
              <Field label="Trial ends (then invoiced)" htmlFor={`trial-end-${sub.id}`}>
                <input id={`trial-end-${sub.id}`} type="date" min={todayStr()} value={date} onChange={e => setDate(e.target.value)} className={inputClass} />
              </Field>
              <PanelButtons onCancel={close} onGo={async () => { if (await act(() => billingService.extendTrial(sub.id, date), `Trial now ends ${date}.`)) close(); }} go="Save" />
            </>
          )}
          {panel === 'suspend' && (
            <>
              <p className="text-sm text-neutral-600">Users lose access until reinstated. Seats and who holds them are kept.</p>
              <Field label="Reason" htmlFor={`susp-${sub.id}`}>
                <input id={`susp-${sub.id}`} value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. INV-0042 60 days overdue" className={inputClass} />
              </Field>
              <PanelButtons onCancel={close} disabled={!reason.trim()} onGo={async () => { if (await act(() => billingService.suspend(sub.id, reason.trim()), `${sub.name} suspended.`)) close(); }} go="Suspend" />
            </>
          )}
          {panel === 'renewal' && (
            <>
              <p className="text-sm text-neutral-600">
                {sub.renewal_invoicing
                  ? 'Invoiced automatically each period at list price less any offer.'
                  : 'Off: this subscription predates automatic renewals. Choose when its next invoice should go out — not a period already paid by hand.'}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Next invoice" htmlFor={`ren-date-${sub.id}`}>
                  <input id={`ren-date-${sub.id}`} type="date" min={todayStr()} value={date} onChange={e => setDate(e.target.value)} className={inputClass} />
                </Field>
                <Field label="Cycle" htmlFor={`ren-cycle-${sub.id}`}>
                  <select id={`ren-cycle-${sub.id}`} value={cycle} onChange={e => setCycle(e.target.value as 'monthly' | 'annual')} className={inputClass}>
                    <option value="monthly">Monthly</option>
                    <option value="annual">Annual</option>
                  </select>
                </Field>
              </div>
              <div className="flex flex-wrap gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={close}>Close</Button>
                {sub.renewal_invoicing && (
                  <Button variant="outline" size="sm" onClick={async () => { if (await act(() => billingService.setRenewal(sub.id, { enabled: false }), 'Renewal invoicing off.')) close(); }}>Turn off</Button>
                )}
                <Button variant="primary" size="sm" onClick={async () => {
                  if (await act(() => billingService.setRenewal(sub.id, { enabled: true, next_billing_date: date, billing_cycle: cycle }), `Next invoice ${date}.`)) close();
                }}>{sub.renewal_invoicing ? 'Save' : 'Turn on'}</Button>
              </div>
            </>
          )}
          {panel === 'seats' && (
            <>
              <p className="text-sm text-neutral-600">Changes seat counts <strong>without raising an invoice</strong> — for corrections and comps. The customer's own purchases are invoiced.</p>
              {sub.seats.map(s => (
                <div key={s.type_key} className="flex items-center justify-between gap-2">
                  <label htmlFor={`seat-${sub.id}-${s.type_key}`} className="text-sm text-neutral-700 min-w-0 break-words">{s.name} <span className="text-neutral-400">({s.assigned_seats} in use)</span></label>
                  <input id={`seat-${sub.id}-${s.type_key}`} type="number" min={s.assigned_seats} value={seats[s.type_key] ?? 0}
                    onChange={e => setSeats(v => ({ ...v, [s.type_key]: Math.max(0, parseInt(e.target.value) || 0) }))}
                    className="w-20 flex-shrink-0 text-center border border-neutral-300 rounded px-1 py-1 text-sm" />
                </div>
              ))}
              <PanelButtons onCancel={close} onGo={async () => {
                const next = Object.entries(seats).map(([type_key, total_seats]) => ({ type_key, total_seats }));
                if (await act(() => billingService.setSeatsWithoutInvoice(sub.id, orgId, next), 'Seats updated (no invoice).')) close();
              }} go="Save seats" />
            </>
          )}
          {panel === 'cancel' && (
            <>
              <p className="text-sm text-error-700">Cancelling revokes every user's seat on {sub.name} straight away.</p>
              <Field label="Reason" htmlFor={`cancel-${sub.id}`}>
                <input id={`cancel-${sub.id}`} value={reason} onChange={e => setReason(e.target.value)} className={inputClass} />
              </Field>
              <PanelButtons onCancel={close} disabled={!reason.trim()} danger onGo={async () => {
                if (await act(() => billingService.cancel(sub.id, orgId, reason.trim()), `${sub.name} cancelled.`)) close();
              }} go="Cancel subscription" />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function PanelButtons({ onCancel, onGo, go, disabled, danger }: { onCancel: () => void; onGo: () => void; go: string; disabled?: boolean; danger?: boolean }) {
  return (
    <div className="flex flex-wrap gap-2 justify-end">
      <Button variant="outline" size="sm" onClick={onCancel}>Close</Button>
      <Button variant="primary" size="sm" onClick={onGo} disabled={disabled} className={danger ? '!bg-error-600 hover:!bg-error-700' : undefined}>{go}</Button>
    </div>
  );
}

function OffersCard({ data, plans, act, orgTz }: { data: BillingOrgDetail; plans: SubscriptionPlan[]; act: Act; orgTz: string }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<TermDraft>(emptyTermDraft());
  const problem = termDraftProblem(draft);
  const today = todayStr();

  const state = (t: BillingOrgDetail['terms'][number]) =>
    t.revoked_at ? 'Revoked' : t.ends_on && t.ends_on < today ? 'Ended' : t.starts_on > today ? 'Upcoming' : 'In force';

  return (
    <Card title="Offers" action={<Button variant="primary" size="sm" onClick={() => setAdding(v => !v)}><Plus className="h-4 w-4" />Add offer</Button>}>
      {adding && (
        <div className="mb-5 p-4 rounded-lg bg-neutral-50 border border-neutral-200 space-y-3">
          <TermFields draft={draft} onChange={setDraft} plans={plans} idPrefix="bo-term" />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => { setAdding(false); setDraft(emptyTermDraft()); }}>Cancel</Button>
            <Button variant="primary" size="sm" disabled={!!problem} title={problem ?? undefined} onClick={async () => {
              if (await act(() => billingService.addTerm(data.organisation.id, termDraftToInput(draft)), 'Offer added.')) {
                setAdding(false); setDraft(emptyTermDraft());
              }
            }}>Add</Button>
          </div>
        </div>
      )}
      {data.terms.length === 0 ? (
        <p className="text-sm text-neutral-500">List price. No discounts or free periods.</p>
      ) : (
        <ul className="divide-y divide-neutral-100">
          {data.terms.map(t => {
            const st = state(t);
            return (
              <li key={t.id} className={`py-3 flex flex-wrap items-start justify-between gap-2 ${st === 'Revoked' || st === 'Ended' ? 'opacity-60' : ''}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-neutral-900 break-words">{describeTerm(t)}</p>
                  <p className="text-xs text-neutral-500 break-words">
                    {st} · from {formatLocalDateInTz(t.starts_on, orgTz)} · {t.reason} · {t.created_by_username ?? 'unknown'}
                    {t.revoked_at && ` · revoked by ${t.revoked_by_username ?? 'unknown'}`}
                  </p>
                </div>
                {!t.revoked_at && st !== 'Ended' && (
                  <Button variant="ghost" size="sm" onClick={() => {
                    if (confirm(`Revoke "${describeTerm(t)}"? Invoices from now on are at list price.`)) {
                      act(() => billingService.revokeTerm(t.id), 'Offer revoked.');
                    }
                  }}>Revoke</Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

const TX_LABELS: Record<string, string> = {
  purchase: 'Purchase', renewal: 'Renewal', trial_conversion: 'Trial converted', trial_start: 'Trial started',
  trial_extended: 'Trial changed', terms_added: 'Offer added', terms_revoked: 'Offer revoked', suspended: 'Suspended',
  reinstated: 'Reinstated', renewals_on: 'Renewals on', renewals_off: 'Renewals off', cancellation: 'Cancelled',
  onboarding: 'Onboarded',
};

function HistoryCard({ data, orgTz }: { data: BillingOrgDetail; orgTz: string }) {
  return (
    <Card title="History">
      {data.transactions.length === 0 ? (
        <p className="text-sm text-neutral-500">Nothing yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-neutral-500 border-b border-neutral-200">
              <tr>
                <th className="text-left py-2 pr-3 font-medium">When</th>
                <th className="text-left py-2 pr-3 font-medium">What</th>
                <th className="text-left py-2 pr-3 font-medium">Detail</th>
                <th className="text-right py-2 pr-3 font-medium">Amount</th>
                <th className="text-left py-2 font-medium">Invoice</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {data.transactions.map(t => (
                <tr key={t.id}>
                  <td className="py-2 pr-3 whitespace-nowrap">{formatDateInTz(t.created_at, orgTz)}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{TX_LABELS[t.transaction_type] ?? t.transaction_type}</td>
                  <td className="py-2 pr-3 min-w-[16rem]">
                    {t.description}
                    {(t.reason || t.actor_username) && (
                      <span className="block text-xs text-neutral-500">{[t.reason, t.actor_username].filter(Boolean).join(' · ')}</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap">{t.amount != null ? money(t.amount) : ''}</td>
                  <td className="py-2 whitespace-nowrap">
                    {(t.xero_invoice_status || t.xero_invoice_id) && (
                      <span className="inline-flex items-center gap-2">
                        <InvoiceStatusBadge status={t.xero_invoice_status} dueDate={t.xero_due_date} />
                        {t.xero_invoice_id && (
                          <a href={xeroInvoiceUrl(t.xero_invoice_id)} target="_blank" rel="noreferrer" className="text-primary-700 hover:underline inline-flex items-center gap-1">
                            {t.xero_invoice_number} <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
